import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import SearchableSelect from '../../components/SearchableSelect';

const CreateFormation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [surveys, setSurveys] = useState([]);

  // chef_terrain runs the F2F terrain, resp_cati the CATI plateau. Both pick from the local survey
  // list — already scoped to their own nature server-side — instead of the whole LimeSurvey
  // catalogue, which would let them attach (and silently auto-create as CATI) a survey of the wrong
  // nature and then lose the formation from their own list.
  const restrictedToLocalSurveys = user?.role === 'chef_terrain' || user?.role === 'resp_cati';

  // Their audience is known in advance; it stays editable, just pre-selected.
  const defaultAudience =
    user?.role === 'chef_terrain' ? 'supervisor' :
    user?.role === 'resp_cati' ? 'interviewer' : 'all';
  
  // LimeSurvey list, searched by name in the select below
  const [limeSurveys, setLimeSurveys] = useState([]);
  const [limeLoading, setLimeLoading] = useState(false);
  const [selectedLimeSurvey, setSelectedLimeSurvey] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    survey_id: '',
    limesurvey_id: '',
    visibility: 'private',
    target_audience: defaultAudience,
    is_active: true,
  });

  useEffect(() => {
    fetchSurveys();
    if (!restrictedToLocalSurveys) {
      fetchLimeSurveys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSurveys = async () => {
    try {
      console.log('Fetching local surveys...');
      const response = await api.get('/formations/surveys/list?active_only=true');
      console.log('Local surveys response:', response.data);
      if (response.data.success) {
        const localSurveys = (response.data.data || []).map(survey => ({
          ...survey,
          id: String(survey.id),
          survey_id: survey.id,
          active: survey.is_active,
        }));
        setSurveys(localSurveys);
        console.log('Local surveys loaded:', localSurveys.length);
      } else {
        console.warn('Local surveys returned success=false');
        setSurveys([]);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setSurveys([]);
    }
  };

  // Load the LimeSurvey catalogue once; the select filters it locally by name or ID.
  const fetchLimeSurveys = async () => {
    setLimeLoading(true);
    try {
      const response = await api.get('/formations/limesurvey/search');

      if (response.data.success) {
        const surveysData = response.data.data || [];
        const surveysArray = Array.isArray(surveysData) ? surveysData : Object.values(surveysData);
        const options = surveysArray
          .map(survey => ({ ...survey, id: String(survey.limesurvey_id) }))
          .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'fr', { sensitivity: 'base' }));
        setLimeSurveys(options);
      } else {
        console.warn('LimeSurvey list returned success=false:', response.data.message);
        setLimeSurveys([]);
      }
    } catch (error) {
      console.error('Error loading LimeSurvey surveys:', error);
      setLimeSurveys([]);
      if (error.response?.status === 403) {
        setError('Accès non autorisé à la recherche LimeSurvey');
      } else {
        setError('Erreur lors du chargement des enquêtes LimeSurvey');
      }
    } finally {
      setLimeLoading(false);
    }
  };

  const surveyOptions = restrictedToLocalSurveys ? surveys : limeSurveys;

  const handleLimeSurveyChange = (optionId) => {
    const survey = optionId ? surveyOptions.find(item => item.id === String(optionId)) : null;
    setSelectedLimeSurvey(survey || null);
    setFormData(prev => ({
      ...prev,
      limesurvey_id: survey ? survey.limesurvey_id : '',
      // A local survey is sent by its own id, so nothing is auto-created; a LimeSurvey pick has
      // no local row yet and travels as limesurvey_id + survey_title.
      survey_id: survey && survey.survey_id ? survey.survey_id : '',
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // If survey_id is selected from dropdown, clear limesurvey_id
    if (name === 'survey_id' && value) {
      const selectedSurvey = surveys.find(s => s.id.toString() === value);
      if (selectedSurvey) {
        setFormData(prev => ({
          ...prev,
          survey_id: value,
          limesurvey_id: selectedSurvey.limesurvey_id,
        }));
        setSelectedLimeSurvey(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.limesurvey_id) {
      setError('Veuillez sélectionner une enquête LimeSurvey');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // If limesurvey_id is selected but no survey_id, send survey_title for auto-creation
      const payload = {
        ...formData,
        survey_id: formData.survey_id || null,
        limesurvey_id: formData.limesurvey_id || null,
        survey_title: selectedLimeSurvey ? selectedLimeSurvey.title : null,
      };

      await api.post('/formations', payload);
      navigate('/formations');
    } catch (error) {
      console.error('Error creating formation:', error);
      setError(error.response?.data?.message || 'Erreur lors de la création de la formation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-3 sm:p-4 md:p-6 animate-slide-up">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-4 sm:mb-5 md:mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
              NOUVELLE FORMATION / BRIEF
            </h1>
          </div>

          {/* Form */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-5 md:p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                />
              </div>

              {/* LimeSurvey survey picker (search by name) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enquête LimeSurvey <span className="text-red-600">*</span>
                </label>
                <SearchableSelect
                  options={surveyOptions}
                  selectedValue={selectedLimeSurvey ? selectedLimeSurvey.id : null}
                  onChange={handleLimeSurveyChange}
                  loading={restrictedToLocalSurveys ? false : limeLoading}
                  placeholder="-- Sélectionner une enquête --"
                  searchPlaceholder="Rechercher par nom ou ID..."
                  getOptionLabel={(survey) => survey.title || `(sans titre) ${survey.limesurvey_id}`}
                  getOptionMeta={(survey) => `ID: ${survey.limesurvey_id} | Statut: ${survey.active ? 'Actif' : 'Inactif'}`}
                  triggerClassName="px-4 py-2"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {restrictedToLocalSurveys
                    ? "Recherchez l'enquête par son nom (ou son ID). Seules les enquêtes de votre périmètre sont proposées."
                    : "Recherchez l'enquête par son nom (ou son ID) dans LimeSurvey. Si l'enquête n'existe pas dans la base locale, elle sera créée automatiquement."}
                </p>
                {selectedLimeSurvey && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm text-green-800">
                      <strong>Sélectionné:</strong> {selectedLimeSurvey.title}
                    </div>
                    <div className="text-xs text-green-600">
                      ID: {selectedLimeSurvey.limesurvey_id}
                      {selectedLimeSurvey.language ? ` | Langue: ${selectedLimeSurvey.language}` : ''}
                    </div>
                  </div>
                )}
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibilité
                </label>
                <select
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                >
                  <option value="private">Privé — réservé aux personnes affectées</option>
                  <option value="public">Public — ouvert à tout utilisateur connecté</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Privé : seuls les affectés (et les chargés d'étude / admins) peuvent ouvrir la formation et ses ressources.
                  Public : n'importe quel utilisateur connecté peut les ouvrir. Dans les deux cas, la formation n'apparaît
                  dans « Mes formations » que pour les personnes qui y sont affectées.
                </p>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Audience cible
                </label>
                <select
                  name="target_audience"
                  value={formData.target_audience}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                >
                  <option value="all">Tous</option>
                  <option value="interviewer">Enquêteurs</option>
                  <option value="controller">Contrôleurs</option>
                  <option value="supervisor">Prestataires</option>
                </select>
              </div>

              {/* Is Active */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Formation active
                </label>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/formations')}
                  className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="mr-2" />
                      Créer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateFormation;

